import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { BaseServices } from '../../kuba.services';

/**
 * Businesses allowed to see the work environment survey while it is still being
 * built out.
 *
 * TEMPORARY (#6073, review comment on #6086). The module is not yet registered
 * in `Application.FeaturesTemplate`, so the tab is appended client-side in
 * `employee-tabs.component.ts` and would otherwise show up for every business.
 * This list keeps it to the businesses we demo from until the feature string —
 * and with it real per-business toggling — exists.
 *
 * Add an id with a comment naming the business, so the list stays readable.
 * Delete this whole file together with `appendWorkEnvironmentSurveyTab` once the
 * feature key is in the feature string; the feature tree then decides.
 */
const DEMO_BUSINESS_IDS: number[] = [
  5, // Kuba Norge AS — the internal business, per features.component.ts
  2626 // Demo Support in both frontend and backend
];

/** `app/business/<id>/...` — the business segment of an in-app URL. */
const BUSINESS_SEGMENT = /\/business\/(\d+)(\/|$)/;

/**
 * The business the user is looking at, taken from the URL.
 *
 * Deliberately not `BaseServices.BusinessId`: that is the *logged-in user's own*
 * business. It comes from `sessionStorage.userSettings`, which login writes once
 * and nothing rewrites when an Admin or Portal user navigates into someone
 * else's business — so it stays put for the whole session. Gating on it let the
 * tab through for every business an admin opened, as long as the admin's own
 * business was on the list (review comment on #6086).
 *
 * `kuba.routing.ts` mounts every business-scoped route under `business/:id`, so
 * the URL is the one thing that does follow what is on screen. Falls back to the
 * user's own business for the paths that carry no business segment — my page and
 * the portal views, where their own business is the right answer.
 */
function viewedBusinessId(url: string): number {
  const match = BUSINESS_SEGMENT.exec(url);

  if (match !== null) {
    return Number(match[1]);
  }

  return Number(BaseServices.BusinessId);
}

/**
 * Whether the business in `url` may use the module.
 *
 * Pass the URL being navigated to. A guard has it as `state.url`; anything
 * rendering after navigation can use `window.location.pathname`, which is what
 * `canUseWorkEnvironmentSurvey` does.
 */
export function canUseWorkEnvironmentSurveyForUrl(url: string): boolean {
  const businessId = viewedBusinessId(url);

  // 0 and NaN both land here: an admin at application level is inside no
  // business at all, and BusinessId is empty before login.
  if (!businessId || isNaN(businessId)) {
    return false;
  }

  return DEMO_BUSINESS_IDS.indexOf(businessId) > -1;
}

/** Whether the business currently on screen may use the module. */
export function canUseWorkEnvironmentSurvey(): boolean {
  return canUseWorkEnvironmentSurveyForUrl(window.location.pathname);
}

/**
 * Route guard for the module.
 *
 * Hiding the tab is not enough on its own — the path stays typable, and #6073
 * asks for the route to be blocked rather than merely hidden. A business that is
 * not on the list is sent to the not-found page, the same destination the app
 * uses for a route that should not exist for the caller.
 */
export const workEnvironmentSurveyGuard: CanActivateFn = (route, state) => {
  // state.url, not window.location: during a guard the address bar still holds
  // the page being navigated away from.
  if (canUseWorkEnvironmentSurveyForUrl(state.url)) {
    return true;
  }

  inject(Router).navigate(['/not-found']);
  return false;
};
